import * as React from "react";
import { formatMessage } from "lib/globalize-util";
import { Map } from "immutable";
import classNames from "classnames";
import debounce from "lodash/debounce";
import { ValueTypes } from "constants/product-collection";

interface IFieldProps {
    value: string;
    field: Map<string, any>;
    readOnly: boolean;
    onChange: any;
    onChangeFocus: any;
    onKeyDown: (e /*KeyboardEvent<HTMLDivElement>*/) => void;
    allowedValueFields: any;
    valueType: any;
}

interface IState {
    focus: boolean;
    displayedValue: string;
}

export class SelectField extends React.PureComponent<IFieldProps, IState> {
    private editor;

    constructor(props) {
        super(props);
        this.state = {
            focus: false,
            displayedValue: props.value || "",
        };
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        // Do not update if typing
        if (!this.state.focus) {
            this.setState({
                displayedValue: nextProps.value || "",
            });
        }
    }

    render() {
        const { field, readOnly, value, allowedValueFields, onKeyDown, valueType } = this.props;
        const { focus, displayedValue } = this.state;
        const fieldName = field.get("field_name");
        const displayPlaceholderText = displayedValue === "";
        const valueMatches =
            allowedValueFields[fieldName] && allowedValueFields[fieldName][displayedValue.toUpperCase()];
        const availableOptions = [1, 0];
        return (
            <div
                className={classNames("fieldContainer", { isFocused: focus, editable: !readOnly })}
                onClick={this.handleClick}
            >
                <span className="fieldLabel">{field.get("display_name")}</span>
                {!readOnly && (
                    <div className="inputContainer" onKeyDown={onKeyDown}>
                        <select
                            data-test={`${fieldName}-menu`}
                            ref={this.bindEditor}
                            name={fieldName}
                            onChange={this.handleChange}
                            onFocus={this.handleFocus}
                            onBlur={this.handleBlur}
                            className={classNames({ displayPlaceholderText })}
                            value={displayedValue}
                        >
                            {valueMatches === undefined && (
                                <option value={displayedValue} disabled={true}>
                                    {displayedValue}
                                </option>
                            )}
                            <option value="" disabled={true}>
                                {this.getPlaceholderText()}
                            </option>
                            {valueType === ValueTypes.BOOL &&
                                availableOptions.map((val) => {
                                    return (
                                        <option key={val} value={val}>
                                            {val ? formatMessage("Yes") : formatMessage("No")}
                                        </option>
                                    );
                                })}
                            {valueType !== ValueTypes.BOOL &&
                                field.getIn(["constraint", "allowed_values"]).map((val) => {
                                    const selectOption = val.get("string_value");
                                    return (
                                        <option key={selectOption} value={selectOption}>
                                            {selectOption}
                                        </option>
                                    );
                                })}
                        </select>
                    </div>
                )}
                {readOnly && <span className="fieldValue nonEditable">{value}</span>}
            </div>
        );
    }

    private bindEditor = (ref) => (this.editor = ref);

    private getPlaceholderText = () => {
        return formatMessage("Select value from list");
    };

    private handleClick = () => {
        process.nextTick(() => {
            if (this.editor && !this.state.focus) {
                this.editor.focus();
            }
        });
    };

    private handleFocus = () => {
        this.setState({ focus: true });
    };

    private handleBlur = () => {
        this.setState({ focus: false });
        this.handleSaveChanges.cancel();
        const { field, onChange, value = "" } = this.props;
        const { displayedValue } = this.state;
        if (value !== displayedValue) {
            onChange({ value: displayedValue, field });
        }
    };

    private handleChange = ({ target }) => {
        const { field } = this.props;
        let { value } = target;
        if (this.props.valueType === ValueTypes.BOOL) {
            value = value === "0" ? false : true;
            this.setState({
                displayedValue: !value ? formatMessage("No") : formatMessage("Yes"),
            });
        } else {
            this.setState({
                displayedValue: value,
            });
        }
        this.handleSaveChanges({ value, field });
    };

    // tslint:disable-next-line: member-ordering
    private handleSaveChanges = debounce(({ value, field }) => this.props.onChange({ value, field }), 600);
}
